import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Bell, BriefcaseBusiness, CalendarClock, Check, CheckSquare, ChevronDown, ClipboardList,
  FileText, FolderOpen, LayoutDashboard, Link2, LogOut, Menu, MessageCircle, MessageSquare, Plug, Plus, Search, Send,
  Settings2, Sparkles, ThumbsDown, ThumbsUp, Timer, Users, X,
} from "lucide-react";
import "@/App.css";
import { AIPromptBox, Todos, Integrations, Communication, ProjectDetail, CreativesLibrary } from "@/pages";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

const nav = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "team", label: "Team members", icon: Users },
  { id: "projects", label: "Projects", icon: BriefcaseBusiness },
  { id: "tasks", label: "Task management", icon: ClipboardList },
  { id: "todo", label: "To-do", icon: CheckSquare },
  { id: "creatives", label: "Creatives folder", icon: FolderOpen },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "communication", label: "Communication", icon: MessageCircle },
];

const CATEGORIES = [
  "Campaign creation", "Campaign launch", "Campaign changes", "Creative brief",
  "Create a design", "Video editing", "Website changes", "Weekly review",
  "Integration", "Taking accesses", "Client escalation", "Retention marketing",
  "Client communication",
];

const TASK_STATUSES = ["In queue", "Initiated", "Work in progress", "In review", "Completed"];
const STATUS_KEY = (s) => (s || "").toLowerCase().replaceAll(" ", "-");

const initials = (name) => (name || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

const errorText = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d || "Something went wrong";
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (d - new Date()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 60) return diff < 0 ? "just now" : "in a moment";
  if (abs < 3600) return `${Math.round(abs / 60)}m ${diff < 0 ? "ago" : ""}`.trim();
  if (abs < 86400) return `${Math.round(abs / 3600)}h ${diff < 0 ? "ago" : ""}`.trim();
  return d.toLocaleDateString();
};

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocal = (v) => (v ? new Date(v).toISOString() : "");

function Avatar({ name }) { return <span className="avatar">{initials(name)}</span>; }
function Pill({ children, tone = "neutral" }) { return <span className={`pill ${tone}`}>{children}</span>; }

function Modal({ title, children, close, size }) {
  return (
    <div className="modal-backdrop" onClick={close}>
      <div className={size === "wide" ? "modal wide" : "modal"} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-button" onClick={close} data-testid="modal-close-button"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Fields({ fields, value, setValue }) {
  return fields.map(([key, label, type]) => (
    <label key={key}>
      {label}
      <input
        type={type || "text"}
        value={value[key] || ""}
        onChange={(e) => setValue({ ...value, [key]: e.target.value })}
        data-testid={`form-${key}-input`}
      />
    </label>
  ));
}

/* ---------- Auth ---------- */
function googleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("performance@tryemveto.com");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      onLogin(r.data);
    } catch (err) { setError(errorText(err)); }
  };
  return (
    <main className="auth-shell">
      <div className="auth-art">
        <div className="brand-mark"><img src="/emveto-logo.png" alt="EMVETO" className="brand-logo lg" /></div>
        <div className="auth-copy">
          <p className="eyebrow">EMVETO AGENCY</p>
          <h1>Move great work<br /><em>forward.</em></h1>
          <p>One calm, connected workspace for the campaigns, creatives and people that grow your clients.</p>
        </div>
        <div className="art-note"><Sparkles size={16} /> Performance, without the busywork</div>
      </div>
      <section className="auth-form">
        <div className="form-inner">
          <div className="brand-lockup"><img src="/emveto-logo.png" alt="EMVETO" className="brand-logo" /><b>EMVETO</b><span className="brand-muted">ops</span></div>
          <p className="eyebrow">WELCOME BACK</p>
          <h2>Good morning.</h2>
          <p className="muted">Sign in to your agency workspace.</p>

          <button
            type="button"
            className="google-button"
            onClick={googleSignIn}
            data-testid="google-signin-button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>

          <div className="or-divider"><span>or</span></div>

          <form onSubmit={submit}>
            <label>Email address<input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
            <label>Password<input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
            {error && <div className="error" data-testid="login-error">{error}</div>}
            <button className="primary-button" data-testid="login-submit-button">Enter workspace <span>↗</span></button>
          </form>
          <p className="form-foot">Your workspace is private to your team.</p>
        </div>
      </section>
    </main>
  );
}

/* ---------- Page shell ---------- */
function Page({ title, eyebrow, subtitle, action, children }) {
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          {subtitle && <p className="muted">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

/* ---------- Overview ---------- */
function Overview({ tasks, projects, user, go }) {
  const attention = [...tasks].sort((a, b) => (new Date(a.deadline) - new Date(b.deadline))).slice(0, 5);
  return (
    <Page
      title={`Good day, ${(user?.name || "there").split(" ")[0]} ✦`}
      eyebrow={new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }).toUpperCase()}
      subtitle="Here’s what’s moving across your agency today."
      action={<button className="primary-button compact" onClick={() => go("tasks")} data-testid="overview-create-task-button"><Plus size={17} /> New task</button>}
    >
      <div className="stats-grid">
        {[
          ["Active projects", projects.length, "+2 this month"],
          ["Open tasks", tasks.filter((x) => x.status !== "Completed").length, `${tasks.filter(t => t.extension_requests?.some(r => r.status === 'pending')).length} pending review`],
          ["On track", "84%", "↑ 6.4%"],
          ["Team capacity", "72%", "Healthy"],
        ].map(([label, value, note]) => (
          <div className="stat" key={label} data-testid={`stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
            <div className="stat-top"><span>{label}</span><span className="trend">{note}</span></div>
            <strong>{value}</strong>
            <div className="sparkline"><i /><i /><i /><i /><i /><i /></div>
          </div>
        ))}
      </div>
      <div className="content-grid">
        <section className="panel">
          <div className="panel-head">
            <div><p className="eyebrow">WORK PULSE</p><h3>What needs attention</h3></div>
            <button className="text-button" onClick={() => go("tasks")} data-testid="overview-view-all-tasks">View all →</button>
          </div>
          {attention.map((t, i) => (
            <div className="attention-row" key={t.id} data-testid={`attention-task-${t.id}`}>
              <span className={`task-dot dot-${i % 4}`}><ClipboardList size={15} /></span>
              <div className="row-main">
                <b>{t.title}</b>
                <small>{t.project} · {t.assignee} · {fmtDate(t.deadline)}</small>
              </div>
              <Pill tone={(t.priority || "").toLowerCase()}>{t.priority}</Pill>
            </div>
          ))}
        </section>
        <section className="panel">
          <div className="panel-head"><div><p className="eyebrow">CLIENT PORTFOLIO</p><h3>Project health</h3></div></div>
          {projects.map((p) => (
            <div className="project-line" key={p.id}>
              <span className="project-logo">{p.brand[0]}</span>
              <div className="row-main"><b>{p.brand}</b><small>{p.services.join(" · ")}</small></div>
              <div className="health"><span>{p.health}%</span><i style={{ width: `${p.health}%` }} /></div>
            </div>
          ))}
        </section>
      </div>
      <section className="panel integrations-panel">
        <div className="panel-head">
          <div><p className="eyebrow">DATA LAYER</p><h3>Connected channels</h3></div>
          <button className="text-button" onClick={() => go("projects")} data-testid="overview-manage-connections">Manage connections →</button>
        </div>
        <div className="integration-row">
          {[["M", "Meta Ads", "Connected"], ["G", "Google Ads", "Connected"], ["A", "GA4", "Connected"], ["S", "Shopify", "Setup required"]].map(([letter, name, status]) => (
            <div className="integration" key={name}>
              <span className="integration-logo">{letter}</span>
              <div><b>{name}</b><small className={status === "Connected" ? "connected" : "warning-text"}>{status}</small></div>
            </div>
          ))}
        </div>
      </section>
    </Page>
  );
}

/* ---------- Team ---------- */
function Team({ team, user, reload }) {
  const isAdmin = user?.role === "Admin";
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const emptyForm = () => ({ name: "", email: "", role: "Performance marketing" });
  const [form, setForm] = useState(emptyForm());
  const openEdit = (m) => { setEditing(m); setForm({ name: m.name, email: m.email, role: m.role }); setOpen(true); setError(""); };
  const openAdd = () => { setEditing(null); setForm(emptyForm()); setOpen(true); setError(""); };
  const save = async () => {
    setError("");
    try {
      if (editing) await api.patch(`/team/${editing.id}`, form);
      else await api.post("/team", form);
      setOpen(false); setEditing(null); setForm(emptyForm()); reload();
    } catch (e) { setError(errorText(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the workspace?`)) return;
    try { await api.delete(`/team/${m.id}`); reload(); } catch (e) { alert(errorText(e)); }
  };
  const resend = async (m) => {
    try { await api.post(`/team/${m.id}/resend-invite`); alert(`Invite resent to ${m.email}`); }
    catch (e) { alert(errorText(e)); }
  };
  return (
    <Page
      title="Team members" eyebrow="PEOPLE & PERMISSIONS" subtitle={isAdmin ? "You have full access to invite, edit and remove teammates." : "The people behind the work."}
      action={isAdmin && <button className="primary-button compact" onClick={openAdd} data-testid="team-add-button"><Plus size={17} /> Add member</button>}
    >
      {open && (
        <Modal title={editing ? `Edit ${editing.name}` : "Add team member"} close={() => { setOpen(false); setEditing(null); }}>
          <Fields fields={[["name", "Full name"], ["email", "Work email", "email"]]} value={form} setValue={setForm} />
          <label>Department
            <select data-testid="team-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option>Performance marketing</option>
              <option>Graphic designing</option>
              <option>Video editing</option>
              <option>Website development</option>
              <option>Admin</option>
              <option>Supervisor</option>
            </select>
          </label>
          {error && <div className="error" data-testid="team-error">{error}</div>}
          <button className="primary-button" data-testid="team-save-button" onClick={save}>{editing ? "Save changes" : "Invite member"}</button>
        </Modal>
      )}
      {team.length === 0 && (
        <div className="empty-hint" data-testid="team-empty">
          <b>No team members yet.</b>
          <span>{isAdmin ? "Click Add member to invite your first teammate." : "Waiting on your admin to invite the team."}</span>
        </div>
      )}
      <div className="member-grid">
        {team.map((m) => (
          <div className="member-card" key={m.id} data-testid={`team-member-${m.id}`}>
            <Avatar name={m.name} />
            <h3>{m.name}</h3>
            <p>{m.role}</p>
            <small>{m.email}</small>
            <div className="member-foot">
              <Pill tone="success">{m.status}</Pill>
              {isAdmin && (
                <div className="admin-actions">
                  {m.status === "Invited" && (
                    <button className="icon-button" onClick={() => resend(m)} data-testid={`team-resend-${m.id}`} title="Resend invite"><Send size={14} /></button>
                  )}
                  <button className="icon-button" onClick={() => openEdit(m)} data-testid={`team-edit-${m.id}`} title="Edit"><Settings2 size={15} /></button>
                  <button className="icon-button danger" onClick={() => remove(m)} data-testid={`team-delete-${m.id}`} title="Remove"><X size={15} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Page>
  );
}

/* ---------- Projects ---------- */
function Projects({ projects, team, user, reload, openProject }) {
  const isAdmin = user?.role === "Admin";
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const ACCENT_PRESETS = ["#5a1ef5", "#e11d74", "#f97316", "#10b981", "#0ea5e9", "#eab308", "#8b5cf6", "#0f172a"];
  const [form, setForm] = useState({
    brand: "", website: "", services: ["Meta Ads"],
    poc: team[0]?.name || "", supervisor: team[1]?.name || team[0]?.name || "",
    accent_color: "#5a1ef5",
    connections: { meta: "Setup required", google: "Setup required", ga4: "Setup required", shopify: "Setup required" },
  });
  const toggle = (s) => setForm({ ...form, services: form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s] });
  const save = async () => {
    setError("");
    try { await api.post("/projects", form); setOpen(false); reload(); }
    catch (e) { setError(errorText(e)); }
  };
  const remove = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete project “${p.brand}”? This cannot be undone.`)) return;
    try { await api.delete(`/projects/${p.id}`); reload(); } catch (err) { alert(errorText(err)); }
  };
  return (
    <Page
      title="Projects" eyebrow="CLIENT PORTFOLIO" subtitle="Every brand, service and connection in one view."
      action={isAdmin && <button className="primary-button compact" onClick={() => setOpen(true)} data-testid="projects-create-button"><Plus size={17} /> New project</button>}
    >
      {open && (
        <Modal title="Create a project" close={() => setOpen(false)}>
          <Fields fields={[["brand", "Brand name"], ["website", "Website link"]]} value={form} setValue={setForm} />
          <label>Services
            <div className="check-grid">
              {["Meta Ads", "Google Ads", "Retention", "Creatives", "Website"].map((s) => (
                <button type="button" key={s} className={form.services.includes(s) ? "check-chip selected" : "check-chip"} onClick={() => toggle(s)} data-testid={`project-service-${s.toLowerCase().replaceAll(" ", "-")}`}>{s}</button>
              ))}
            </div>
          </label>
          <div className="two-col">
            <label>POC
              <select data-testid="project-poc-select" value={form.poc} onChange={(e) => setForm({ ...form, poc: e.target.value })}>
                {team.map((x) => <option key={x.id}>{x.name}</option>)}
              </select>
            </label>
            <label>Supervisor
              <select data-testid="project-supervisor-select" value={form.supervisor} onChange={(e) => setForm({ ...form, supervisor: e.target.value })}>
                {team.map((x) => <option key={x.id}>{x.name}</option>)}
              </select>
            </label>
          </div>
          <label>Brand accent
            <div className="accent-picker">
              {ACCENT_PRESETS.map((c) => (
                <button
                  type="button"
                  key={c}
                  className={form.accent_color === c ? "accent-swatch selected" : "accent-swatch"}
                  style={{ background: c }}
                  onClick={() => setForm({ ...form, accent_color: c })}
                  data-testid={`project-accent-${c.replace('#', '')}`}
                  aria-label={c}
                />
              ))}
              <input
                type="color"
                value={form.accent_color}
                onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                data-testid="project-accent-input"
                className="accent-native"
              />
            </div>
          </label>
          {error && <div className="error" data-testid="project-error">{error}</div>}
          <button className="primary-button" data-testid="project-save-button" onClick={save}>Create project</button>
        </Modal>
      )}
      <div className="project-cards">
        {projects.length === 0 && (
          <div className="empty-hint" data-testid="projects-empty">
            <b>No projects yet.</b>
            <span>{isAdmin ? "Click New project to add your first client brand." : "Waiting on your admin to create the first project."}</span>
          </div>
        )}
        {projects.map((p) => {
          const accent = p.accent_color || "#5a1ef5";
          return (
            <div className="client-card" key={p.id} data-testid={`project-card-${p.id}`} style={{ borderTop: `3px solid ${accent}`, cursor: "pointer" }} onClick={() => openProject?.(p.id)}>
              {isAdmin && (
                <button className="card-delete" onClick={(e) => remove(p, e)} data-testid={`project-delete-${p.id}`} title="Delete project"><X size={14} /></button>
              )}
              <div className="client-card-head">
                <span className="project-logo large" style={{ background: accent + "22", color: accent }}>{p.brand[0]}</span>
                <div><h3>{p.brand}</h3><small>{p.website}</small></div>
              </div>
              <div className="service-list">{p.services.map((s) => <Pill key={s}>{s}</Pill>)}</div>
              <div className="health-block">Account health <b style={{ color: accent }}>{p.health}%</b><i style={{ width: `${p.health}%`, background: `linear-gradient(90deg, ${accent}77, ${accent})` }} /></div>
              <div className="connection-list">
                {Object.entries(p.connections || {}).map(([key, value]) => (
                  <div key={key}><span>{key.toUpperCase()}</span><small className={value === "Connected" ? "connected" : "warning-text"}>{value}</small></div>
                ))}
              </div>
              <div className="client-foot"><span>POC <b>{p.poc}</b></span><span className="sup">Supervisor <b>{p.supervisor}</b></span></div>
            </div>
          );
        })}
      </div>
    </Page>
  );
}

/* ---------- Tasks ---------- */
function TaskDetail({ task, projects, user, close, reload }) {
  const project = projects.find((p) => p.brand === task.project);
  const canSupervise = user?.role === "Admin" || project?.supervisor === user?.name;
  const [feedback, setFeedback] = useState("");
  const [reqOpen, setReqOpen] = useState(false);
  const [reqDeadline, setReqDeadline] = useState(toDatetimeLocal(task.deadline));
  const [reqReason, setReqReason] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const pendingExt = (task.extension_requests || []).find((r) => r.status === "pending");
  const decidedExt = (task.extension_requests || []).filter((r) => r.status !== "pending");

  const addFeedback = async () => {
    if (!feedback.trim()) return;
    setBusy(true); setError("");
    try { await api.post(`/tasks/${task.id}/feedback`, { content: feedback }); setFeedback(""); await reload(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  const submitExtension = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/tasks/${task.id}/extension-request`, {
        requested_deadline: fromDatetimeLocal(reqDeadline),
        reason: reqReason,
      });
      setReqOpen(false); setReqReason(""); await reload();
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  const decide = async (approve) => {
    if (!pendingExt) return;
    setBusy(true); setError("");
    try {
      await api.post(`/tasks/${task.id}/extension-request/${pendingExt.id}/${approve ? "approve" : "reject"}`, { note });
      setNote(""); await reload();
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (newStatus) => {
    setBusy(true); setError("");
    try { await api.patch(`/tasks/${task.id}`, { status: newStatus }); await reload(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  const latestTask = task; // reload will re-render with fresh data via parent

  return (
    <Modal size="wide" title={task.title} close={close}>
      <div className="task-detail" data-testid={`task-detail-${task.id}`}>
        <div className="task-detail-meta">
          <div><span>Project</span><b>{task.project}</b></div>
          <div><span>Category</span><b>{task.category}</b></div>
          <div><span>Assignee</span><b>{task.assignee}</b></div>
          <div><span>Deadline</span><b>{fmtDate(task.deadline)}</b></div>
          <div><span>Priority</span><b>{task.priority}</b></div>
          <div><span>Status</span><b>{task.status}</b></div>
          <div><span>Supervisor</span><b>{project?.supervisor || "—"}</b></div>
        </div>

        {task.context && <p className="task-context">{task.context}</p>}

        <div className="task-status-row">
          {TASK_STATUSES.map((s) => (
            <button
              key={s}
              className={task.status === s ? "status-chip active" : "status-chip"}
              disabled={busy}
              onClick={() => toggleStatus(s)}
              data-testid={`task-set-status-${s.toLowerCase().replaceAll(" ", "-")}`}
            >
              {s}
            </button>
          ))}
        </div>

        {error && <div className="error" data-testid="task-error">{error}</div>}

        <section className="detail-section">
          <div className="detail-head"><MessageSquare size={15} /> <b>Comments & feedback</b></div>
          <div className="feedback-list">
            {(task.feedback || []).length === 0 && <p className="muted small">No comments yet. Ask questions or share progress here.</p>}
            {(task.feedback || []).map((f) => (
              <div className="feedback-item" key={f.id} data-testid={`feedback-${f.id}`}>
                <Avatar name={f.author} />
                <div>
                  <div className="fb-head"><b>{f.author}</b><small>{fmtRelative(f.created_at)}</small></div>
                  <p>{f.content}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="feedback-composer">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Add a comment, ask a question, share a link…"
              data-testid="feedback-input"
            />
            <button className="primary-button compact" onClick={addFeedback} disabled={busy || !feedback.trim()} data-testid="feedback-submit-button">Post comment</button>
          </div>
        </section>

        <section className="detail-section">
          <div className="detail-head"><Timer size={15} /> <b>Deadline extension</b></div>

          {pendingExt ? (
            <div className="ext-card pending" data-testid={`extension-pending-${pendingExt.id}`}>
              <div className="ext-head">
                <Pill tone="warning">Pending review</Pill>
                <small>{fmtRelative(pendingExt.created_at)}</small>
              </div>
              <p><b>{pendingExt.requester}</b> requested a new deadline.</p>
              <div className="ext-swap">
                <div><span>Current</span><b>{fmtDate(pendingExt.current_deadline)}</b></div>
                <div><span>Requested</span><b>{fmtDate(pendingExt.requested_deadline)}</b></div>
              </div>
              <p className="ext-reason">“{pendingExt.reason}”</p>
              {canSupervise ? (
                <>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note for the requester (optional)"
                    data-testid="extension-decision-note"
                  />
                  <div className="ext-actions">
                    <button className="ghost-button" disabled={busy} onClick={() => decide(false)} data-testid="extension-reject-button"><ThumbsDown size={14} /> Decline</button>
                    <button className="primary-button compact" disabled={busy} onClick={() => decide(true)} data-testid="extension-approve-button"><ThumbsUp size={14} /> Approve</button>
                  </div>
                </>
              ) : (
                <p className="muted small">Only {project?.supervisor || "the supervisor"} or an admin can decide this request.</p>
              )}
            </div>
          ) : reqOpen ? (
            <div className="ext-card">
              <label>New requested deadline
                <input
                  type="datetime-local"
                  value={reqDeadline}
                  onChange={(e) => setReqDeadline(e.target.value)}
                  data-testid="extension-deadline-input"
                />
              </label>
              <label>Reason
                <textarea value={reqReason} onChange={(e) => setReqReason(e.target.value)} placeholder="Why the deadline needs to move" data-testid="extension-reason-input" />
              </label>
              <div className="ext-actions">
                <button className="ghost-button" onClick={() => setReqOpen(false)} data-testid="extension-cancel-button">Cancel</button>
                <button className="primary-button compact" onClick={submitExtension} disabled={busy || !reqReason.trim() || !reqDeadline} data-testid="extension-submit-button">Request extension</button>
              </div>
            </div>
          ) : (
            <button className="ghost-button" onClick={() => setReqOpen(true)} data-testid="extension-open-button"><Timer size={14} /> Request extension</button>
          )}

          {decidedExt.length > 0 && (
            <div className="ext-history">
              <p className="eyebrow small">HISTORY</p>
              {decidedExt.map((r) => (
                <div className={`ext-past ${r.status}`} key={r.id} data-testid={`extension-past-${r.id}`}>
                  <Pill tone={r.status === "approved" ? "success" : "danger"}>{r.status}</Pill>
                  <div>
                    <b>{r.requester}</b> requested {fmtDate(r.requested_deadline)}
                    <small>Decided by {r.reviewer || "—"} · {fmtRelative(r.decided_at)}</small>
                    {r.review_note && <p>“{r.review_note}”</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}

function Tasks({ tasks, projects, team, user, reload, openTaskId, setOpenTaskId }) {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [moveError, setMoveError] = useState("");
  const [justDropped, setJustDropped] = useState(null); // { taskId, status }
  const [memberView, setMemberView] = useState("All"); // admin/supervisor scope filter
  const isAdmin = user?.role === "Admin";
  const isSupervisor = useMemo(() => {
    if (!user) return false;
    if (user.role === "Supervisor") return true;
    const name = user.name || "";
    return projects.some((p) => p.supervisor === name);
  }, [user, projects]);
  const canScopeToMember = isAdmin || isSupervisor;
  const WIP_LIMIT = 8;
  const emptyForm = () => ({
    title: "", category: "Campaign creation",
    project: projects[0]?.brand || "",
    assignee: team[0]?.name || "",
    date: "", time: "",
    priority: "Medium",
    context: "",
  });
  const [form, setForm] = useState(emptyForm);

  const shown = useMemo(() => {
    return tasks.filter((t) => {
      if (category !== "All" && t.category !== category) return false;
      if (canScopeToMember && memberView !== "All" && t.assignee !== memberView) return false;
      if (query && !`${t.title} ${t.project} ${t.assignee} ${t.category}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [tasks, category, query, canScopeToMember, memberView]);

  const columns = useMemo(() => {
    const map = Object.fromEntries(TASK_STATUSES.map((s) => [s, []]));
    for (const t of shown) {
      const key = TASK_STATUSES.includes(t.status) ? t.status : "In queue";
      map[key].push(t);
    }
    return map;
  }, [shown]);

  const openTask = tasks.find((t) => t.id === openTaskId);

  const buildISO = () => {
    if (!form.date) return "";
    const timeStr = form.time || "18:00";
    return new Date(`${form.date}T${timeStr}`).toISOString();
  };

  const save = async () => {
    setError("");
    try {
      const deadline = buildISO();
      if (!deadline) { setError("Pick a deadline date"); return; }
      await api.post("/tasks", {
        title: form.title, category: form.category, project: form.project,
        assignee: form.assignee, deadline, priority: form.priority, context: form.context,
      });
      setOpen(false);
      setForm(emptyForm());
      reload();
    } catch (e) { setError(errorText(e)); }
  };

  const aiFill = (data) => {
    let dateStr = "", timeStr = "";
    if (data.deadline) {
      const d = new Date(data.deadline);
      if (!isNaN(d)) {
        const pad = (n) => String(n).padStart(2, "0");
        dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    const cat = CATEGORIES.includes(data.category) ? data.category : form.category;
    const project = projects.some((p) => p.brand === data.project) ? data.project : form.project;
    const assignee = team.some((t) => t.name === data.assignee) ? data.assignee : form.assignee;
    const priority = ["Low", "Medium", "High"].includes(data.priority) ? data.priority : form.priority;
    setForm({
      ...form,
      title: data.title || form.title,
      category: cat,
      project,
      assignee,
      date: dateStr || form.date,
      time: timeStr || form.time,
      priority,
      context: data.context || form.context,
    });
  };

  const moveTask = async (taskId, newStatus) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t || t.status === newStatus) return;
    setMoveError("");
    setJustDropped({ taskId, status: newStatus });
    setTimeout(() => setJustDropped((prev) => (prev && prev.taskId === taskId ? null : prev)), 550);
    try {
      await api.patch(`/tasks/${taskId}`, { status: newStatus });
      await reload();
    } catch (e) { setMoveError(errorText(e)); }
  };

  const deleteTask = async (taskId, e) => {
    e.stopPropagation();
    const t = tasks.find((x) => x.id === taskId);
    if (!t) return;
    if (!window.confirm(`Delete “${t.title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      await reload();
    } catch (err) {
      setMoveError(errorText(err));
    }
  };

  const noProjectsOrTeam = projects.length === 0 || team.length === 0;

  return (
    <Page
      title="Task management" eyebrow="THE WORK QUEUE" subtitle="Drag and drop tasks across the pipeline."
      action={<button className="primary-button compact" onClick={() => setOpen(true)} disabled={noProjectsOrTeam} data-testid="tasks-create-button"><Plus size={17} /> Create task</button>}
    >
      {open && (
        <Modal title="Create a task" close={() => setOpen(false)}>
          <AIPromptBox api={api} kind="task" onFill={aiFill} />
          <Fields fields={[["title", "Task title"]]} value={form} setValue={setForm} />
          <label>Category
            <select data-testid="task-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((x) => <option key={x}>{x}</option>)}
            </select>
          </label>
          <label>Project
            <select data-testid="task-project-select" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
              {projects.map((x) => <option key={x.id}>{x.brand}</option>)}
            </select>
          </label>
          <label>Assign to
            <select data-testid="task-assignee-select" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })}>
              {team.map((x) => <option key={x.id}>{x.name}</option>)}
            </select>
          </label>
          <div className="two-col">
            <label>Deadline date
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="task-date-input" />
            </label>
            <label>Deadline time
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="task-time-input" />
            </label>
          </div>
          <label>Priority
            <select data-testid="task-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option>Low</option><option>Medium</option><option>High</option>
            </select>
          </label>
          <label>Context (optional)
            <textarea value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} data-testid="task-context-input" placeholder="Campaign, ad set or brief details…" />
          </label>
          {error && <div className="error" data-testid="task-create-error">{error}</div>}
          <button className="primary-button" data-testid="task-save-button" onClick={save}>Create task</button>
        </Modal>
      )}

      {noProjectsOrTeam && (
        <div className="empty-hint" data-testid="tasks-empty-hint">
          <b>Set up your workspace first.</b>
          <span>Add at least one team member and one project before creating tasks.</span>
        </div>
      )}

      <div className="task-toolbar">
        <div className="category-scroll" data-testid="task-category-filter">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              className={category === c ? "cat-chip active" : "cat-chip"}
              onClick={() => setCategory(c)}
              data-testid={`task-cat-${STATUS_KEY(c)}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="toolbar-right">
          {canScopeToMember && (
            <label className="member-scope" data-testid="kanban-member-scope">
              <Users size={13} />
              <select value={memberView} onChange={(e) => setMemberView(e.target.value)} data-testid="kanban-member-select">
                <option value="All">Everyone</option>
                {team.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </label>
          )}
          <div className="search-box"><Search size={15} /><input data-testid="task-search-input" placeholder="Search tasks" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
        </div>
      </div>

      {moveError && <div className="error" data-testid="kanban-move-error">{moveError}</div>}

      <div className="kanban-board" data-testid="kanban-board">
        {TASK_STATUSES.map((s) => {
          const overLimit = columns[s].length > WIP_LIMIT;
          return (
          <div
            key={s}
            className={`kanban-col ${dragOverCol === s ? "drag-over" : ""} ${overLimit ? "over-wip" : ""}`}
            data-testid={`kanban-col-${STATUS_KEY(s)}`}
            onDragOver={(e) => { e.preventDefault(); setDragOverCol(s); }}
            onDragLeave={() => setDragOverCol(null)}
            onDrop={(e) => { e.preventDefault(); setDragOverCol(null); if (dragId) { moveTask(dragId, s); setDragId(null); } }}
          >
            <div className="kanban-col-head">
              <span className={`col-dot col-${STATUS_KEY(s)}`} />
              <b>{s}</b>
              <span className={`col-count ${overLimit ? "wip-warn" : ""}`} data-testid={`kanban-count-${STATUS_KEY(s)}`}>
                {columns[s].length}{overLimit ? ` / ${WIP_LIMIT}` : ""}
              </span>
            </div>
            {overLimit && (
              <div className="wip-banner" data-testid={`kanban-wip-warn-${STATUS_KEY(s)}`}>
                WIP limit reached ({columns[s].length} &gt; {WIP_LIMIT}). Ship or reassign a few before adding more.
              </div>
            )}
            <div className="kanban-col-body">
              {columns[s].length === 0 && <div className="kanban-empty">Drop tasks here</div>}
              {columns[s].map((t) => {
                const pending = (t.extension_requests || []).some((r) => r.status === "pending");
                const dropped = justDropped && justDropped.taskId === t.id && justDropped.status === s;
                return (
                  <div
                    key={t.id}
                    className={`kanban-card ${dragId === t.id ? "dragging" : ""} ${dropped ? "just-dropped" : ""}`}
                    draggable
                    onDragStart={(e) => { setDragId(t.id); try { e.dataTransfer.effectAllowed = "move"; } catch (_) {} }}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => setOpenTaskId(t.id)}
                    data-testid={`kanban-card-${t.id}`}
                  >
                    {isAdmin && (
                      <button
                        className="kc-delete"
                        onClick={(e) => deleteTask(t.id, e)}
                        title="Delete task"
                        aria-label={`Delete ${t.title}`}
                        data-testid={`kanban-delete-${t.id}`}
                      >
                        <X size={12} />
                      </button>
                    )}
                    <div className="kc-head">
                      <span className={`prio prio-${(t.priority || "medium").toLowerCase()}`}>{t.priority}</span>
                      <small>{t.category}</small>
                    </div>
                    <b className="kc-title">{t.title}</b>
                    <div className="kc-meta">
                      <span><Avatar name={t.assignee} />{t.assignee}</span>
                    </div>
                    <div className="kc-foot">
                      <small><CalendarClock size={12} /> {fmtDate(t.deadline)}</small>
                      <small>{t.project}</small>
                    </div>
                    {(pending || (t.feedback || []).length > 0) && (
                      <div className="kc-badges">
                        {pending && <Pill tone="warning">Extension pending</Pill>}
                        {(t.feedback || []).length > 0 && <Pill>{t.feedback.length} comment{t.feedback.length > 1 ? "s" : ""}</Pill>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );})}
      </div>

      <div className="reminder-note">
        <Bell size={16} />
        <div>
          <b>Smart reminders are on</b>
          <span>Assignees get email + in-app nudges 24h, 3h, 1h, 30m and 10m before each deadline.</span>
        </div>
      </div>

      {openTask && <TaskDetail task={openTask} projects={projects} user={user} reload={reload} close={() => setOpenTaskId(null)} />}
    </Page>
  );
}

/* ---------- Brief ---------- */
function Brief({ website, projects, team, reload }) {
  const types = website ? ["Home page", "Collection page", "Product page", "Cart page", "Checkout page"] : ["Static", "Carousel", "Video"];
  const [form, setForm] = useState({
    kind: website ? "Home page" : "Static",
    project: projects[0]?.brand || "",
    assignee: "",
    purpose: "", details: "", references: "",
  });
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const save = async () => {
    setError(""); setSaved(false);
    try { await api.post("/briefs", form); setSaved(true); reload(); }
    catch (e) { setError(errorText(e)); }
  };
  const aiFill = (data) => {
    setForm({
      ...form,
      kind: types.includes(data.kind) ? data.kind : form.kind,
      project: projects.some((p) => p.brand === data.project) ? data.project : form.project,
      assignee: team.some((t) => t.name === data.assignee) ? data.assignee : form.assignee,
      purpose: data.purpose || form.purpose,
      details: data.details || form.details,
      references: data.references || form.references,
    });
  };
  return (
    <Page title={website ? "Website brief" : "Prepare a brief"} eyebrow={website ? "DIGITAL EXPERIENCE" : "CREATIVE PRODUCTION"} subtitle="Give the team a clear creative north star.">
      <div className="brief-layout">
        <section className="brief-form">
          <AIPromptBox api={api} kind="brief" onFill={aiFill} />
          <label>{website ? "Page type" : "Media type"}
            <div className="option-grid">
              {types.map((x) => (
                <button className={form.kind === x ? "option selected" : "option"} onClick={() => setForm({ ...form, kind: x })} key={x} data-testid={`brief-type-${x.toLowerCase().replaceAll(" ", "-")}`}>
                  {x}{form.kind === x && <Check size={15} />}
                </button>
              ))}
            </div>
          </label>
          <div className="two-col">
            <label>Project
              <select data-testid="brief-project-select" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
                {projects.map((x) => <option key={x.id}>{x.brand}</option>)}
              </select>
            </label>
            <label>Assign to (optional)
              <select data-testid="brief-assignee-select" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })}>
                <option value="">— Nobody yet —</option>
                {team.map((x) => <option key={x.id}>{x.name}</option>)}
              </select>
            </label>
          </div>
          <label>Purpose<textarea data-testid="brief-purpose-input" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="What should this work achieve?" /></label>
          <label>{website ? "Website brief" : "Creative brief"}<textarea className="big-textarea" data-testid="brief-details-input" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Add the brief, context and deliverables..." /></label>
          <label>Reference links<textarea data-testid="brief-references-input" value={form.references} onChange={(e) => setForm({ ...form, references: e.target.value })} placeholder="Paste links, one per line" /></label>
          {error && <div className="error" data-testid="brief-error">{error}</div>}
          {saved && <div className="success" data-testid="brief-saved">Brief saved ✓</div>}
          <button className="primary-button" data-testid="brief-save-button" onClick={save}>Save brief ↗</button>
        </section>
        <aside className="brief-preview">
          <p className="eyebrow">LIVE PREVIEW</p>
          <div className="preview-art"><Sparkles size={18} /><span>{form.kind} brief</span></div>
          <p className="eyebrow">{form.project || "SELECT PROJECT"}{form.assignee ? ` · ${form.assignee}` : ""}</p>
          <h2>{form.purpose || "Your brief starts here"}</h2>
          <p className="muted">{form.details || "Fill in the details to give your team a clear creative north star."}</p>
        </aside>
      </div>
    </Page>
  );
}

/* ---------- Notifications ---------- */
function NotificationPanel({ items, unread, close, onMarkAll, onRead, jumpToTask }) {
  return (
    <div className="notif-panel" data-testid="notification-panel">
      <div className="notif-head">
        <div>
          <p className="eyebrow">NOTIFICATIONS</p>
          <h3>Inbox <span className="unread-count">{unread}</span></h3>
        </div>
        <button className="text-button" onClick={onMarkAll} data-testid="notifications-mark-all">Mark all read</button>
      </div>
      <div className="notif-list">
        {items.length === 0 && <p className="muted small" data-testid="notifications-empty">You’re all caught up. ✨</p>}
        {items.map((n) => (
          <button
            key={n.id}
            className={`notif-item ${n.read ? "read" : "unread"}`}
            onClick={() => { onRead(n.id); if (n.task_id) jumpToTask(n.task_id); close(); }}
            data-testid={`notification-${n.id}`}
          >
            <div className="notif-body">
              <b>{n.title}</b>
              <p>{n.detail}</p>
              <small>{fmtRelative(n.created_at)}</small>
            </div>
            {!n.read && <span className="notif-dot" />}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- App ---------- */
function App() {
  const [user, setUser] = useState(null);
  const [section, setSection] = useState("overview");
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [openTaskId, setOpenTaskId] = useState(null);
  const [authBooting, setAuthBooting] = useState(true);
  const [authError, setAuthError] = useState("");

  const reload = async () => {
    try {
      const [a, b, c, n, br] = await Promise.all([
        api.get("/team"), api.get("/projects"), api.get("/tasks"), api.get("/notifications"), api.get("/briefs"),
      ]);
      setTeam(a.data); setProjects(b.data); setTasks(c.data); setNotifs(n.data); setBriefs(br.data);
    } catch (e) { /* ignore transient */ }
  };

  const openProject = (id) => { setSelectedProjectId(id); setSection("project-detail"); };
  const backToProjects = () => { setSelectedProjectId(null); setSection("projects"); };

  useEffect(() => {
    // Detect Google OAuth callback: session_id in URL fragment
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      const sid = decodeURIComponent(match[1]);
      // Clear the fragment immediately so refresh doesn't re-exchange
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
      api.post("/auth/session", { session_id: sid })
        .then((r) => { setUser(r.data); setSection("overview"); reload(); })
        .catch((e) => { setAuthError(errorText(e)); })
        .finally(() => setAuthBooting(false));
      return;
    }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); setSection("overview"); reload(); })
      .catch(() => {})
      .finally(() => setAuthBooting(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [user]);

  const markAll = async () => { await api.post("/notifications/read-all").catch(() => {}); reload(); };
  const readOne = async (id) => { await api.post(`/notifications/${id}/read`).catch(() => {}); reload(); };
  const jumpToTask = (id) => { setSection("tasks"); setOpenTaskId(id); };

  if (authBooting) {
    return (
      <main className="auth-boot" data-testid="auth-booting">
        <div className="brand-lockup"><img src="/emveto-logo.png" alt="EMVETO" className="brand-logo" /><b>EMVETO</b><span className="brand-muted">ops</span></div>
        <p className="muted">Signing you in…</p>
      </main>
    );
  }

  if (!user) return <Login onLogin={(u) => { setUser(u); setSection("overview"); reload(); if (authError) setAuthError(""); }} />;
  const current = nav.find((x) => x.id === section) || nav[0];

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    setUser(null); setSection("overview"); setTeam([]); setProjects([]); setTasks([]); setNotifs({ items: [], unread: 0 });
  };

  return (
    <div className="app-shell">
      <aside className={mobile ? "sidebar mobile-open" : "sidebar"}>
        <div className="sidebar-brand">
          <img src="/emveto-logo.png" alt="EMVETO" className="brand-logo" /><b>EMVETO</b><span className="brand-muted">ops</span>
          <button className="mobile-close" onClick={() => setMobile(false)} data-testid="mobile-close-button"><X size={17} /></button>
        </div>
        <div className="workspace-switch">
          <span className="workspace-avatar">E</span>
          <div><b>EMVETO Agency</b><small>Agency workspace</small></div>
          <ChevronDown size={15} />
        </div>
        <nav>
          {nav.map((item) => {
            const Icon = item.icon;
            const count = item.id === "tasks" ? tasks.filter(t => t.status !== "Completed").length : null;
            return (
              <button
                className={section === item.id ? "nav-item active" : "nav-item"}
                onClick={() => { setSection(item.id); setMobile(false); }}
                key={item.id}
                data-testid={`nav-${item.id}`}
              >
                <Icon size={17} />{item.label}
                {count != null && <span className="nav-count">{count}</span>}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          <div className="upgrade-note">
            <Sparkles size={16} />
            <b>Make space for the work</b>
            <small>Plan your team’s next focus.</small>
            <button onClick={() => setSection("tasks")} data-testid="sidebar-plan-button">Plan a task →</button>
          </div>
          <button className="nav-item" data-testid="nav-settings"><Settings2 size={17} />Workspace settings</button>
          <button className="profile-row" onClick={logout} data-testid="logout-button">
            <Avatar name={user.name} />
            <span><b>{user.name}</b><small>{user.role}</small></span>
            <LogOut size={15} />
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <button className="mobile-menu" onClick={() => setMobile(true)} data-testid="mobile-menu-button"><Menu size={19} /></button>
          <div className="breadcrumbs"><span>Workspace</span><span>/</span><b>{current.label}</b></div>
          <div className="top-actions">
            <div className="notif-wrap">
              <button className="icon-button" onClick={() => setNotifOpen((v) => !v)} data-testid="notifications-button">
                <Bell size={18} />
                {notifs.unread > 0 && <i />}
              </button>
              {notifOpen && (
                <NotificationPanel
                  items={notifs.items}
                  unread={notifs.unread}
                  close={() => setNotifOpen(false)}
                  onMarkAll={markAll}
                  onRead={readOne}
                  jumpToTask={jumpToTask}
                />
              )}
            </div>
            <Avatar name={user.name} />
          </div>
        </header>
        {section === "overview" && <Overview tasks={tasks} projects={projects} user={user} go={setSection} />}
        {section === "team" && <Team team={team} user={user} reload={reload} />}
        {section === "projects" && <Projects projects={projects} team={team} user={user} reload={reload} openProject={openProject} />}
        {section === "project-detail" && selectedProjectId && projects.find(p => p.id === selectedProjectId) && (
          <ProjectDetail
            api={api}
            project={projects.find(p => p.id === selectedProjectId)}
            user={user}
            team={team}
            briefs={briefs}
            back={backToProjects}
            reload={reload}
          />
        )}
        {section === "tasks" && <Tasks tasks={tasks} projects={projects} team={team} user={user} reload={reload} openTaskId={openTaskId} setOpenTaskId={setOpenTaskId} />}
        {section === "todo" && <Todos api={api} user={user} team={team} />}
        {section === "creatives" && <CreativesLibrary api={api} projects={projects} user={user} openProject={openProject} />}
        {section === "integrations" && <Integrations api={api} projects={projects} user={user} />}
        {section === "communication" && <Communication api={api} user={user} team={team} />}
      </main>
    </div>
  );
}
export default App;
